import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { routingJobsApi } from '../../api/routingJobsApi';
import { userManagementApi } from '../../api/userManagementApi';
import {
  getModelTypeLabel,
  getTenantModelTypeOptions,
  ModelTypeOption,
} from '../../modules/tenantRegistry';

interface RouteConfigModalProps {
  open: boolean;
  onClose: () => void;
  depotId: string;
  tenantId?: string;
  modelType?: string;
  configFilesMap?: Record<string, string>;
  onSaved?: () => Promise<void> | void;
}

const PRY_VEHICLE_TYPE_COST_KEY = 'vehicle_type_cost';
const SOURCE_LATITUDE_KEY = 'sourceLatitude';
const SOURCE_LONGITUDE_KEY = 'sourceLongitude';
const TIMEZONE_KEY = 'timezone';
const DEFAULT_TIMEZONE = 'Asia/Kolkata';

const normalizeModelType = (value?: string) => (value || '').trim().toUpperCase();

const uniqueOptions = (options: ModelTypeOption[]): ModelTypeOption[] => {
  const seen = new Set<string>();
  return options.filter((option) => {
    const normalized = normalizeModelType(option.value);
    if (!normalized || seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
};

const RouteConfigModal: React.FC<RouteConfigModalProps> = ({
  open,
  onClose,
  depotId,
  tenantId,
  modelType,
  configFilesMap = {},
  onSaved,
}) => {
  const [selectedModelType, setSelectedModelType] = useState('');
  const [depotConfigFiles, setDepotConfigFiles] = useState<Record<string, string>>({});
  const [sourceLatitude, setSourceLatitude] = useState('');
  const [sourceLongitude, setSourceLongitude] = useState('');
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [algoOptions, setAlgoOptions] = useState<ModelTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configUpload, setConfigUpload] = useState<{
    uploading: boolean;
    fileName?: string;
    error?: string;
  }>({ uploading: false });
  const [downloadingVehicleCost, setDownloadingVehicleCost] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const buildFallbackAlgoOptions = useCallback((): ModelTypeOption[] => {
    const localOptions = getTenantModelTypeOptions(tenantId);
    const currentModelType = normalizeModelType(modelType);
    if (localOptions.length) {
      return uniqueOptions(localOptions);
    }
    if (currentModelType) {
      return [{ value: currentModelType, label: getModelTypeLabel(currentModelType) }];
    }
    return [];
  }, [tenantId, modelType]);

  const loadAlgoOptions = useCallback(async (): Promise<ModelTypeOption[]> => {
    const fallbackOptions = buildFallbackAlgoOptions();
    if (!tenantId) {
      return fallbackOptions;
    }

    try {
      const tenant = await userManagementApi.getTenant(tenantId);
      const allowedOptions = (tenant.allowedModelTypes || []).map((allowedModelType) => {
        const normalized = normalizeModelType(allowedModelType);
        return {
          value: normalized,
          label: getModelTypeLabel(normalized),
        };
      });
      return uniqueOptions(allowedOptions.length ? allowedOptions : fallbackOptions);
    } catch (err) {
      console.warn('Unable to load tenant model types; using local registry fallback:', err);
      return fallbackOptions;
    }
  }, [tenantId, buildFallbackAlgoOptions]);

  const initialize = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSaveError(null);
    setConfigUpload({ uploading: false });

    try {
      const options = await loadAlgoOptions();
      const optionValues = options.map((option) => normalizeModelType(option.value));
      const currentModelType = normalizeModelType(modelType);

      setAlgoOptions(options);
      setSelectedModelType(
        currentModelType && optionValues.includes(currentModelType)
          ? currentModelType
          : optionValues[0] || currentModelType
      );
      setDepotConfigFiles(configFilesMap ?? {});
      setSourceLatitude(getConfigValue(configFilesMap, SOURCE_LATITUDE_KEY));
      setSourceLongitude(getConfigValue(configFilesMap, SOURCE_LONGITUDE_KEY));
      setTimezone(getConfigValue(configFilesMap, TIMEZONE_KEY) || DEFAULT_TIMEZONE);
    } catch (err) {
      console.error('Error loading depot configuration:', err);
      setError('Failed to load depot configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [configFilesMap, loadAlgoOptions, modelType]);

  useEffect(() => {
    if (open) {
      initialize();
    }
  }, [open, initialize]);

  const vehicleCostFileRef = depotConfigFiles[PRY_VEHICLE_TYPE_COST_KEY];

  const validationError = useMemo(() => {
    if (!selectedModelType) {
      return 'Select an algorithm type';
    }
    if (
      algoOptions.length &&
      !algoOptions.some((option) => normalizeModelType(option.value) === selectedModelType)
    ) {
      return 'Algorithm type must be allowed for this tenant';
    }
    const lat = Number(sourceLatitude);
    if (!sourceLatitude.trim() || !Number.isFinite(lat) || lat < -90 || lat > 90) {
      return 'Enter a valid source latitude between -90 and 90';
    }
    const lng = Number(sourceLongitude);
    if (!sourceLongitude.trim() || !Number.isFinite(lng) || lng < -180 || lng > 180) {
      return 'Enter a valid source longitude between -180 and 180';
    }
    if (selectedModelType === 'PRY' && !vehicleCostFileRef) {
      return 'Upload vehicle type cost config for PRY';
    }
    try {
      Intl.DateTimeFormat('en-US', { timeZone: timezone.trim() || DEFAULT_TIMEZONE }).format();
    } catch {
      return 'Enter a valid IANA timezone such as Asia/Kolkata';
    }
    return null;
  }, [algoOptions, selectedModelType, sourceLatitude, sourceLongitude, timezone, vehicleCostFileRef]);

  const vehicleCostValidationError = validationError?.includes('vehicle type cost') ? validationError : null;
  const coordinateValidationError = validationError && !vehicleCostValidationError ? validationError : null;

  const handleVehicleTypeCostUpload = async (file: File) => {
    if (!tenantId || !selectedModelType) {
      setConfigUpload({ uploading: false, fileName: file.name, error: 'Select an algorithm type first' });
      return;
    }

    setConfigUpload({ uploading: true, fileName: file.name });
    setSaveError(null);
    try {
      const response = await routingJobsApi.uploadTenantFile(
        tenantId,
        selectedModelType,
        PRY_VEHICLE_TYPE_COST_KEY,
        file
      );
      const storageRef = response.storagePath || response.storageId;
      setDepotConfigFiles((prev) => ({
        ...prev,
        [PRY_VEHICLE_TYPE_COST_KEY]: storageRef,
      }));
      setConfigUpload({ uploading: false, fileName: file.name });
    } catch (uploadError: any) {
      setConfigUpload({
        uploading: false,
        fileName: file.name,
        error: uploadError?.message || 'Upload failed',
      });
    }
  };

  const handleVehicleTypeCostDownload = async () => {
    if (!vehicleCostFileRef) {
      return;
    }

    setDownloadingVehicleCost(true);
    setSaveError(null);
    setConfigUpload((prev) => ({ ...prev, error: undefined }));
    try {
      const blob = await routingJobsApi.downloadInternalFile(vehicleCostFileRef);
      triggerBrowserDownload(blob, toConfigDownloadFileName(depotId, PRY_VEHICLE_TYPE_COST_KEY));
    } catch (downloadError: any) {
      console.error('Failed to download vehicle type cost config:', downloadError);
      setConfigUpload((prev) => ({
        ...prev,
        error: downloadError?.message || 'Download failed',
      }));
    } finally {
      setDownloadingVehicleCost(false);
    }
  };

  const handleSave = async () => {
    if (validationError) {
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await userManagementApi.updateDepot(depotId, {
        modelType: selectedModelType,
        configFilesMap: {
          ...depotConfigFiles,
          [SOURCE_LATITUDE_KEY]: sourceLatitude.trim(),
          [SOURCE_LONGITUDE_KEY]: sourceLongitude.trim(),
          [TIMEZONE_KEY]: timezone.trim() || DEFAULT_TIMEZONE,
        },
      });
      await onSaved?.();
      onClose();
    } catch (saveErrorValue: any) {
      console.error('Error saving depot configuration:', saveErrorValue);
      setSaveError(saveErrorValue?.message || 'Failed to save depot configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Depot Configuration</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
            <Button sx={{ ml: 2 }} size="small" onClick={initialize}>
              Retry
            </Button>
          </Alert>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            <Box>
              <Typography variant="subtitle2">Depot</Typography>
              <Typography variant="body2" color="text.secondary">
                {depotId}
              </Typography>
            </Box>

            <FormControl fullWidth error={Boolean(validationError && !selectedModelType)}>
              <InputLabel id="depot-config-algo-type-label">Algorithm Type</InputLabel>
              <Select
                labelId="depot-config-algo-type-label"
                value={selectedModelType}
                label="Algorithm Type"
                onChange={(event) => {
                  setSelectedModelType(normalizeModelType(event.target.value));
                  setSaveError(null);
                }}
              >
                {algoOptions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {validationError && !selectedModelType
                  ? validationError
                  : 'Choose one of the model types enabled for this tenant'}
              </FormHelperText>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Source Latitude"
                value={sourceLatitude}
                onChange={(event) => {
                  setSourceLatitude(event.target.value);
                  setSaveError(null);
                }}
                type="number"
                fullWidth
                required
                inputProps={{ min: -90, max: 90, step: 0.000001 }}
                error={Boolean(validationError?.toLowerCase().includes('latitude'))}
                helperText="Depot start latitude"
              />
              <TextField
                label="Source Longitude"
                value={sourceLongitude}
                onChange={(event) => {
                  setSourceLongitude(event.target.value);
                  setSaveError(null);
                }}
                type="number"
                fullWidth
                required
                inputProps={{ min: -180, max: 180, step: 0.000001 }}
                error={Boolean(validationError?.toLowerCase().includes('longitude'))}
                helperText="Depot start longitude"
              />
            </Stack>

            <TextField
              label="Timezone"
              value={timezone}
              onChange={(event) => {
                setTimezone(event.target.value);
                setSaveError(null);
              }}
              fullWidth
              required
              error={Boolean(validationError?.toLowerCase().includes('timezone'))}
              helperText="IANA timezone used to anchor dispatch dates and planned times"
              placeholder={DEFAULT_TIMEZONE}
            />

            {coordinateValidationError && selectedModelType && (
              <Alert severity="error">{coordinateValidationError}</Alert>
            )}

            {selectedModelType === 'PRY' && (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: vehicleCostValidationError || configUpload.error ? 'error.main' : 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2">Vehicle Type Cost</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {configUpload.fileName ||
                          (vehicleCostFileRef ? 'Config file uploaded' : 'No file selected')}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {vehicleCostFileRef && (
                        <Button
                          variant="outlined"
                          startIcon={
                            downloadingVehicleCost ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )
                          }
                          disabled={configUpload.uploading || downloadingVehicleCost}
                          onClick={handleVehicleTypeCostDownload}
                        >
                          Download
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        disabled={configUpload.uploading || downloadingVehicleCost || !tenantId}
                      >
                        {vehicleCostFileRef ? 'Replace' : 'Upload'}
                        <input
                          type="file"
                          accept=".csv"
                          hidden
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                              handleVehicleTypeCostUpload(file);
                            }
                            event.target.value = '';
                          }}
                        />
                      </Button>
                    </Stack>
                  </Stack>
                  {configUpload.uploading && <LinearProgress />}
                  {(vehicleCostValidationError || configUpload.error) && (
                    <Alert severity="error">
                      {configUpload.error || vehicleCostValidationError}
                    </Alert>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading || saving || Boolean(validationError) || configUpload.uploading}
          variant="contained"
        >
          {saving ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RouteConfigModal;

function getConfigValue(config: Record<string, string> | undefined, key: string): string {
  const entry = Object.entries(config ?? {}).find(([candidate]) => candidate.toLowerCase() === key.toLowerCase());
  return entry?.[1] ?? '';
}

function toConfigDownloadFileName(depotId: string, configKey: string): string {
  const sanitize = (value: string) => value.replace(/[^a-zA-Z0-9._-]/g, '_');
  const sanitizedKey = sanitize(configKey);
  const fileName = sanitizedKey.endsWith('.csv') ? sanitizedKey : `${sanitizedKey}.csv`;
  return `${sanitize(depotId)}-${fileName}`;
}

function triggerBrowserDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
